import { CONFIG_ENVIRONMENT, CONFIG_VERSION } from '../../config';
import { LogAppender } from './logger';
import * as Sentry from '@sentry/browser';
import { User } from '@sentry/types';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Reflects, if we should call `setDeviceInfo` before each error
 */
let detailedErrorLogging = false;

/**
 * Set the devices information as sentry context before logging an error
 */
export function setDeviceInfo(): void {

  if (!detailedErrorLogging) return;

  const defaultVal = '-1',
        nav: Navigator = navigator,
        winPerf: Performance =  window.performance,
        connection: any = (nav as any).connection ||
                          (nav as any).mozConnection ||
                          (nav as any).webkitConnection;

  /* istanbul ignore next else */
  if (Sentry && typeof Sentry.setContext === 'function') {
    try {
      Sentry.setContext('device_info', {
        device: {
            memory: {
              available: 'deviceMemory' in nav ? (nav as any).deviceMemory : defaultVal,
              ...(Object.prototype.hasOwnProperty.call(winPerf, 'memory') ? (winPerf as any).memory : {})
            },
            processor: 'hardwareConcurrency' in nav ? nav.hardwareConcurrency : defaultVal
        },
        browser: {
          locale: 'language' in nav ? nav.language : defaultVal,
          cookieEnabled: 'cookieEnabled' in nav ? nav.cookieEnabled : defaultVal,
          doNotTrack: 'doNotTrack' in nav ? nav.doNotTrack : defaultVal,
          size: window.innerWidth + 'x' + window.innerHeight + 'px',
          orientation: 'orientation' in window ? window.orientation : defaultVal,
          timing: {
            ...(Object.prototype.hasOwnProperty.call(winPerf, 'timing') ? winPerf.timing : undefined)
          },
          navigation: {
            ...(Object.prototype.hasOwnProperty.call(winPerf, 'navigation') ? winPerf.navigation : undefined)
          }
        },
        network: {
            online: 'onLine' in nav ? nav.onLine : defaultVal,
            downlink: Object.prototype.hasOwnProperty.call(connection, 'downlink') ? connection.downlink : defaultVal,
            effectiveType: Object.prototype.hasOwnProperty.call(connection, 'effectiveType') ? connection.effectiveType : defaultVal,
            rtt: Object.prototype.hasOwnProperty.call(connection, 'rtt') ? connection.rtt : defaultVal,
            saveData: Object.prototype.hasOwnProperty.call(connection, 'saveData') ? connection.saveData : defaultVal
        }
      });
      // eslint-disable-next-line no-empty
    } catch (ex) {}
  }
}

/**
 * Initialize sentry itself
 * @param {boolea} detailed Whether we should add detailed device information on logging or not
 */
export function initSentry(sentryDsn: string, detailed?: boolean): void {
  try {
    Sentry.init({
      dsn: sentryDsn,
      environment: CONFIG_ENVIRONMENT,
      release: CONFIG_VERSION,
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      debug: CONFIG_ENVIRONMENT !== 'PRODUCTION' ? true : false,
      ignoreErrors: [
        'extension', // Extensions in General
        'moz-extension', // General Firefox Extensions
        'mailvelope-client-api.js', // Anoying Mailvelope Extension
        'BetterJsPop', // https://github.com/nicolauns/BetterJsPop
        'grecaptcha', // Google Recaptcha
        'grecaptcha is not defined', // Google Recaptcha
        'googletag', // Google Tag (Advertising)
        'navigator.geolocation', // Something which want's to access the users device location
        'Cannot redefine property: play', // Something media (video) related
        'Cannot redefine property: FuckAdBlock', // Some AdBlock Detection Script
        'Cannot redefine property: BlockAdBlock', // Some AdBlock Detection Script
        'Cannot redefine property: googletag', // Google Tag (Advertising)
        'can\'t redefine non-configurable property "fuckAdBlock"', // Some AdBlock Detection Script
        'redeclaration of const hideMyLocation', // Whatever this is?
        new RegExp(/\*moz?extension\*/i), // General Firefox Extensions
        new RegExp(/\*moz-extension\*/i), // General Firefox Extensions
      ],
      blacklistUrls: [
        new RegExp(/extension/i),
        new RegExp(/chrome:/i),
        new RegExp(/file:/i),
        new RegExp(/127\.0\.0\.1:4001/i)
      ]
    });
    if (detailed) {
      detailedErrorLogging = true;
    }
  } catch (ex) {
    console.error(ex);
  }
}

/**
 * Sets the users identity for sentry
 * @param {User} user The users identity (tpid) if available
 */
export function setUser(user: User | null): void {
  /* istanbul ignore next else */
  if (Sentry && typeof Sentry.setUser === 'function') {
    Sentry.setUser(user);
  }
}

/**
 * Sets custom sentry tags for logging
 * @param {string} key
 * @param {string} value
 */
export function setTag(key: string, value: string): void {
  /* istanbul ignore next else */
  if (Sentry && typeof Sentry.setTag === 'function') {
    Sentry.setTag(key, value);
  }
}

/**
 * Stringify the data to be passed to sentry
 * @param {any[]} args
 */
export function stringify(args: any[]): string {
  return args.map((item) => `${item instanceof Error ? item.toString() : JSON.stringify(item)}`).join(' ');
}

/**
 * Enables the functionality to append sentry to (f. e.) a logger
 */
export const sentryAppender: LogAppender = {
  warn: (...args) => {
    /* istanbul ignore next else */
    if (Sentry && typeof Sentry.captureMessage === 'function') {
      setDeviceInfo();
      Sentry.captureMessage(stringify(args), Sentry.Severity.Warning);
    }
  },
  error: (...args) => {
    /* istanbul ignore next else */
    if (Sentry && typeof Sentry.captureMessage === 'function') {
      setDeviceInfo();
      Sentry.captureMessage(stringify(args), Sentry.Severity.Error);
    }
  }
};
